/**
 * Looks at a rendered page the way a person would: where is it empty, where is it a flat colour, where is
 * there text, where is there a picture. The page (inside its blank outer margin) is cut into a grid of
 * 10 columns by 20 rows and every cell is given one of five kinds:
 *
 *   blank       nothing there: costs no bytes at all (the page is already white)
 *   background  a flat colour: drawn as one filled rectangle, costs a few bytes
 *   image       a photo or a smooth picture
 *   mixed       text over or next to a picture
 *   text        text and line art
 *   fine        small print, math and other dense text that needs more pixels to stay readable
 *
 * Only image, mixed and text cells need pixels. The compressor shrinks them in that order -- images first,
 * mixed next, text last -- because that is the order in which the eye notices the loss.
 */

export type TileKind = "blank" | "background" | "image" | "mixed" | "text" | "fine";

export interface TileRect {
  x0: number; y0: number; x1: number; y1: number; // pixels, x1/y1 exclusive
  kind: TileKind;
  /** Fill colour of a background rectangle. */
  color?: [number, number, number];
}

export interface PageGrid {
  width: number;
  height: number;
  /** Area that holds anything at all; the margin outside it is left blank. null when the whole page is blank. */
  content: { x0: number; y0: number; x1: number; y1: number } | null;
  cols: number;
  rows: number;
  /** Kind of every cell, row by row. */
  kinds: TileKind[];
  /** Cells joined into rectangles; blank cells are not listed. */
  rects: TileRect[];
}

export const GRID_COLS = 10;
export const GRID_ROWS = 20;
const MIN_TILE = 24;

const BLANK_GRAY = 238;
const COLOR_SPREAD = 24;

function grayAt(d: Uint8ClampedArray, p: number): number {
  return (d[p] * 299 + d[p + 1] * 587 + d[p + 2] * 114) / 1000;
}

function isInk(d: Uint8ClampedArray, p: number): boolean {
  if (grayAt(d, p) < BLANK_GRAY) return true;
  const mx = Math.max(d[p], d[p + 1], d[p + 2]);
  const mn = Math.min(d[p], d[p + 1], d[p + 2]);
  return mx - mn > COLOR_SPREAD;
}

/** Decides what one cell is. Exported for tests. */
export function classifyRegion(d: Uint8ClampedArray, width: number, x0: number, y0: number, x1: number, y1: number): { kind: TileKind; color?: [number, number, number] } {
  const hist = new Array<number>(32).fill(0);
  let n = 0, ink = 0, edges = 0, sr = 0, sg = 0, sb = 0, sg2 = 0, sgs = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const p = (y * width + x) * 4;
      const g = grayAt(d, p);
      hist[Math.min(31, g >> 3)]++;
      n++;
      sgs += g; sg2 += g * g;
      sr += d[p]; sg += d[p + 1]; sb += d[p + 2];
      if (isInk(d, p)) ink++;
      if (x + 1 < x1 && Math.abs(g - grayAt(d, p + 4)) > 40) edges++;
      else if (y + 1 < y1 && Math.abs(g - grayAt(d, p + width * 4)) > 40) edges++;
    }
  }
  if (!n) return { kind: "blank" };
  if (ink / n < 0.004) return { kind: "blank" }; // dust and scanner speckle count as nothing
  // Flat: the middle 96% of the brightness values sit in a narrow band.
  let acc = 0, lo = 0, hi = 31;
  for (let b = 0; b < 32; b++) { acc += hist[b]; if (acc >= n * 0.02) { lo = b; break; } }
  acc = 0;
  for (let b = 31; b >= 0; b--) { acc += hist[b]; if (acc >= n * 0.02) { hi = b; break; } }
  const std = Math.sqrt(Math.max(0, sg2 / n - (sgs / n) * (sgs / n)));
  // Truly flat: a gentle gradient or a soft picture must not be turned into a block of one colour.
  if (std <= 3.5 && (hi - lo) <= 2 && edges / n < 0.002) {
    const color: [number, number, number] = [Math.round(sr / n), Math.round(sg / n), Math.round(sb / n)];
    return { kind: "background", color };
  }
  const whole = shapeOf(hist, n, edges, ink);
  if (whole === "text") return { kind: "text" };
  // Look at the four quarters: text beside a picture is "mixed", even when the picture dominates the cell.
  const mx = (x0 + x1) >> 1, my = (y0 + y1) >> 1;
  const quarters: ("text" | "photo" | "other" | "empty")[] = [];
  for (const [a, b, c, e] of [[x0, y0, mx, my], [mx, y0, x1, my], [x0, my, mx, y1], [mx, my, x1, y1]] as const) {
    quarters.push(quarterShape(d, width, a, b, c, e));
  }
  const hasText = quarters.includes("text"), hasPhoto = quarters.includes("photo");
  if (hasText && hasPhoto) return { kind: "mixed" };
  if (whole === "photo") return { kind: "image" };
  return { kind: "mixed" };
}

function shapeOf(hist: number[], n: number, edges: number, ink: number): "text" | "photo" | "other" {
  let occupied = 0;
  for (let b = 0; b < 32; b++) if (hist[b] >= n * 0.01) occupied++;
  const edgeFrac = edges / n;
  // Few tones with sharp edges, or a little ink on a clean ground: text and line art.
  if (occupied <= 7 && (edgeFrac >= 0.01 || ink / n < 0.05)) return "text";
  // Smooth change from point to point: a photo or a shaded area.
  if (edgeFrac < 0.03) return "photo";
  if (occupied <= 7) return "text";
  return "other";
}

function quarterShape(d: Uint8ClampedArray, width: number, x0: number, y0: number, x1: number, y1: number): "text" | "photo" | "other" | "empty" {
  const hist = new Array<number>(32).fill(0);
  let n = 0, ink = 0, edges = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const p = (y * width + x) * 4;
      const g = grayAt(d, p);
      hist[Math.min(31, g >> 3)]++;
      n++;
      if (isInk(d, p)) ink++;
      if (x + 1 < x1 && Math.abs(g - grayAt(d, p + 4)) > 40) edges++;
      else if (y + 1 < y1 && Math.abs(g - grayAt(d, p + width * 4)) > 40) edges++;
    }
  }
  if (!n || ink / n < 0.004) return "empty";
  return shapeOf(hist, n, edges, ink);
}

/** Joins neighbouring cells of the same kind (and the same fill colour) into as few rectangles as possible. */
export function mergeCells(kinds: TileKind[], colors: ([number, number, number] | undefined)[], cols: number, rows: number, xs: number[], ys: number[]): TileRect[] {
  const keyOf = (i: number) => (kinds[i] === "background" ? `background:${(colors[i] ?? [255, 255, 255]).map((v) => v >> 4).join(",")}` : kinds[i]);
  type Run = { c0: number; c1: number; r0: number; r1: number; key: string; kind: TileKind; color?: [number, number, number] };
  const open = new Map<string, Run>();
  const done: Run[] = [];
  for (let r = 0; r < rows; r++) {
    const next = new Map<string, Run>();
    let c = 0;
    while (c < cols) {
      const i = r * cols + c;
      if (kinds[i] === "blank") { c++; continue; }
      const key = keyOf(i);
      let e = c + 1;
      while (e < cols && kinds[r * cols + e] !== "blank" && keyOf(r * cols + e) === key) e++;
      const span = `${key}|${c}|${e}`;
      const prev = open.get(span);
      if (prev) { prev.r1 = r + 1; next.set(span, prev); open.delete(span); }
      else next.set(span, { c0: c, c1: e, r0: r, r1: r + 1, key, kind: kinds[i], color: colors[i] });
      c = e;
    }
    open.forEach((run) => done.push(run));
    open.clear();
    next.forEach((run, k) => open.set(k, run));
  }
  open.forEach((run) => done.push(run));
  return consolidate(done.map((run) => ({ x0: xs[run.c0], x1: xs[run.c1], y0: ys[run.r0], y1: ys[run.r1], kind: run.kind, color: run.color })));
}

/** Every picture rectangle carries its own JPEG header (about 600 bytes), so fewer, larger rectangles are cheaper. */
const MAX_GROWTH = 2.5;
const overlaps = (a: TileRect, b: TileRect) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
const areaOf = (r: TileRect) => (r.x1 - r.x0) * (r.y1 - r.y0);

export function consolidate(rects: TileRect[]): TileRect[] {
  const out = rects.slice();
  for (;;) {
    let best: { i: number; j: number; growth: number; u: TileRect } | null = null;
    for (let i = 0; i < out.length; i++) {
      if (out[i].kind === "background") continue; // flat fills are cheap as they are
      for (let j = i + 1; j < out.length; j++) {
        if (out[j].kind !== out[i].kind) continue;
        const u: TileRect = { kind: out[i].kind, x0: Math.min(out[i].x0, out[j].x0), y0: Math.min(out[i].y0, out[j].y0), x1: Math.max(out[i].x1, out[j].x1), y1: Math.max(out[i].y1, out[j].y1) };
        const growth = areaOf(u) / (areaOf(out[i]) + areaOf(out[j]));
        if (growth > MAX_GROWTH || (best && growth >= best.growth)) continue;
        if (out.some((o, k) => k !== i && k !== j && overlaps(u, o))) continue;
        best = { i, j, growth, u };
      }
    }
    if (!best) return out;
    out.splice(best.j, 1);
    out[best.i] = best.u;
  }
}

/** Cuts a rendered page (RGBA pixels) into the grid and says what is in each cell. */
export function analysePage(rgba: Uint8ClampedArray, width: number, height: number): PageGrid {
  const rowInk = new Uint32Array(height), colInk = new Uint32Array(width);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (isInk(rgba, (y * width + x) * 4)) { rowInk[y]++; colInk[x]++; }
    }
  }
  const first = (a: Uint32Array) => a.findIndex((v) => v >= 3);
  const last = (a: Uint32Array) => { for (let i = a.length - 1; i >= 0; i--) if (a[i] >= 3) return i; return -1; };
  const x0 = first(colInk), x1 = last(colInk) + 1, y0 = first(rowInk), y1 = last(rowInk) + 1;
  if (x0 < 0 || y0 < 0 || x1 <= x0 || y1 <= y0) return { width, height, content: null, cols: 0, rows: 0, kinds: [], rects: [] };
  const bw = x1 - x0, bh = y1 - y0;
  const cols = Math.max(1, Math.min(GRID_COLS, Math.floor(bw / MIN_TILE)));
  const rows = Math.max(1, Math.min(GRID_ROWS, Math.floor(bh / MIN_TILE)));
  const xs = Array.from({ length: cols + 1 }, (_, i) => Math.round(x0 + (i * bw) / cols));
  const ys = Array.from({ length: rows + 1 }, (_, i) => Math.round(y0 + (i * bh) / rows));
  const kinds: TileKind[] = [];
  const colors: ([number, number, number] | undefined)[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const k = classifyRegion(rgba, width, xs[c], ys[r], xs[c + 1], ys[r + 1]);
      kinds.push(k.kind);
      colors.push(k.color);
    }
  }
  demoteSmallFlats(kinds, colors, cols, rows);
  const rects = mergeCells(kinds, colors, cols, rows, xs, ys).map((r) => (r.kind === "text" && isFineText(rgba, width, r) ? { ...r, kind: "fine" as TileKind } : r));
  return { width, height, content: { x0, y0, x1, y1 }, cols, rows, kinds, rects };
}

/** True when a region has next to no ink: used to drop empty quarters and sixteenths of a page. */
export function isBlankRegion(d: Uint8ClampedArray, width: number, x0: number, y0: number, x1: number, y1: number): boolean {
  const n = (x1 - x0) * (y1 - y0);
  if (n <= 0) return true;
  const limit = Math.max(1, Math.floor(n * 0.004));
  let ink = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      if (isInk(d, (y * width + x) * 4) && ++ink > limit) return false;
    }
  }
  return true;
}

/** Whole page cut into `cols` x `rows` equal cells; the cells that hold something. */
export function nonBlankCells(d: Uint8ClampedArray, width: number, height: number, cols: number, rows: number): { x0: number; y0: number; x1: number; y1: number }[] {
  const out: { x0: number; y0: number; x1: number; y1: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = Math.round((c * width) / cols), x1 = Math.round(((c + 1) * width) / cols);
      const y0 = Math.round((r * height) / rows), y1 = Math.round(((r + 1) * height) / rows);
      if (!isBlankRegion(d, width, x0, y0, x1, y1)) out.push({ x0, y0, x1, y1 });
    }
  }
  return out;
}

/** The page's own paper is near white but never exactly: speckle, shadow and tint. Sets it to pure white so empty paper counts as empty. */
export function whitenPaper(d: Uint8ClampedArray): void {
  for (let p = 0; p < d.length; p += 4) {
    const mx = Math.max(d[p], d[p + 1], d[p + 2]), mn = Math.min(d[p], d[p + 1], d[p + 2]);
    if (mn >= 215 && mx - mn <= 28) { d[p] = d[p + 1] = d[p + 2] = 255; }
  }
}

/**
 * Small print and math need more pixels than ordinary text. Reads the page's text lines: the spacing between lines
 * (line pitch, at the 200 dpi render) says how big the type is, and irregular spacing (no steady rhythm of lines)
 * says it is a formula, a table or labels rather than a paragraph.
 */
export const RENDER_DPI = 200;
const SMALL_PRINT_PITCH = 31; // pixels at 200 dpi: lines closer than this are about 9 pt type or smaller
export function lineRhythm(d: Uint8ClampedArray, width: number, r: { x0: number; y0: number; x1: number; y1: number }): { pitch: number; strength: number } {
  const h = r.y1 - r.y0;
  const prof = new Float64Array(h);
  for (let y = 0; y < h; y++) {
    let c = 0;
    for (let x = r.x0; x < r.x1; x++) if (grayAt(d, ((r.y0 + y) * width + x) * 4) < 160) c++;
    prof[y] = c;
  }
  let mean = 0;
  for (let y = 0; y < h; y++) mean += prof[y];
  mean /= h || 1;
  let v0 = 0;
  for (let y = 0; y < h; y++) { prof[y] -= mean; v0 += prof[y] * prof[y]; }
  if (v0 <= 0) return { pitch: 0, strength: 0 };
  let best = { pitch: 0, strength: 0 };
  for (let lag = 10; lag <= Math.min(120, Math.floor(h / 2)); lag++) {
    let s = 0;
    for (let y = 0; y + lag < h; y++) s += prof[y] * prof[y + lag];
    const v = s / v0;
    if (v > best.strength) best = { pitch: lag, strength: v };
  }
  return best;
}

export function isFineText(d: Uint8ClampedArray, width: number, r: { x0: number; y0: number; x1: number; y1: number }): boolean {
  const h = r.y1 - r.y0;
  if (h < 60) return false; // a heading or a single line: nothing to measure
  const { pitch, strength } = lineRhythm(d, width, r);
  if (strength >= 0.3) return pitch < SMALL_PRINT_PITCH;
  return h >= 200; // a tall block with no steady line rhythm: formulas, tables, labels
}

/** One step of the page-conversion levels, applied to the 10 x 20 grid. Ratios are relative to the 200 dpi render. */
export interface GridSetting { ratio: number; quality: number }
export interface GridStage {
  /** Flat-colour areas: kept as a picture at `flatRatio`, or drawn as a plain fill. */
  flat: "fill" | number;
  image: GridSetting; mixed: GridSetting; text: GridSetting; fine: GridSetting;
}
const dpi = (n: number) => n / RENDER_DPI;
const G = (n: number, quality: number): GridSetting => ({ ratio: dpi(n), quality });
const ALL140 = { image: G(140, 0.8), mixed: G(140, 0.8), text: G(140, 0.8), fine: G(140, 0.85) };
/** Levels 6-11 of the plan, gentlest first. */
export const GRID_STAGES: { name: string; detail: string; stage: GridStage }[] = [
  { name: "Remaining areas at 140 dpi", detail: "Every area that still holds something is stored at 140 dpi.", stage: { flat: dpi(140), ...ALL140 } },
  { name: "Flat-colour areas as plain fills", detail: "Areas of one even colour are not stored as pictures any more; they are drawn as a filled rectangle.", stage: { flat: "fill", ...ALL140 } },
  { name: "Photo and shaded areas at 70 dpi", detail: "Areas where colour varies (photos, shading) drop to 70 dpi.", stage: { flat: "fill", ...ALL140, image: G(70, 0.65) } },
  { name: "Text-with-picture areas at 100 dpi", detail: "Areas that mix text and colour drop to 100 dpi.", stage: { flat: "fill", ...ALL140, image: G(70, 0.65), mixed: G(100, 0.8) } },
  { name: "Text areas at 100 dpi (small print and math at 120 dpi)", detail: "Plain text drops to 100 dpi. Small print and math areas drop to 120 dpi so they stay readable.", stage: { flat: "fill", image: G(70, 0.65), mixed: G(100, 0.8), text: G(100, 0.8), fine: G(120, 0.85) } },
  { name: "Text areas at 85 dpi (final level)", detail: "Plain text drops to 85 dpi, the lowest that is still readable. Small print and math stay at 120 dpi.", stage: { flat: "fill", image: G(70, 0.65), mixed: G(100, 0.8), text: G(85, 0.8), fine: G(120, 0.85) } },
];

export function stageSetting(stage: GridStage, kind: TileKind): GridSetting | "fill" | null {
  if (kind === "background") return stage.flat === "fill" ? "fill" : { ratio: stage.flat, quality: 0.7 };
  if (kind === "image") return stage.image;
  if (kind === "mixed") return stage.mixed;
  if (kind === "text") return stage.text;
  if (kind === "fine") return stage.fine;
  return null;
}


/** A small flat patch inside a picture is part of the picture: drawn as a block of colour it would show as a visible square. */
const MIN_FLAT_CELLS = 6;
export function demoteSmallFlats(kinds: TileKind[], colors: ([number, number, number] | undefined)[], cols: number, rows: number): void {
  const key = (i: number) => (colors[i] ?? [255, 255, 255]).map((v) => v >> 4).join(",");
  const seen = new Uint8Array(kinds.length);
  for (let s0 = 0; s0 < kinds.length; s0++) {
    if (seen[s0] || kinds[s0] !== "background") continue;
    const k = key(s0);
    const comp: number[] = [];
    const stack = [s0];
    seen[s0] = 1;
    while (stack.length) {
      const i = stack.pop()!;
      comp.push(i);
      const r = Math.floor(i / cols), c = i % cols;
      for (const [rr, cc] of [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]]) {
        if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) continue;
        const j = rr * cols + cc;
        if (!seen[j] && kinds[j] === "background" && key(j) === k) { seen[j] = 1; stack.push(j); }
      }
    }
    if (comp.length < MIN_FLAT_CELLS) for (const i of comp) { kinds[i] = "image"; colors[i] = undefined; }
  }
}
